import { Box, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

interface ClickableEntityNameProps {
  name: string;
  entityId: string;
  onEntityClick?: (entityId: string) => void;
  variant?: "body1" | "body2" | "caption";
  fontWeight?: number | string;
  description?: string;
  descriptionVariant?: "body1" | "body2" | "caption";
  descriptionColor?: string;
  mode?: "overlay" | "standalone";
}

const ClickableEntityName: React.FC<ClickableEntityNameProps> = ({
  name,
  entityId,
  onEntityClick,
  variant = "body2",
  fontWeight = "normal",
  description,
  descriptionVariant = "caption",
  descriptionColor = "text.secondary",
  mode = "overlay",
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (mode === "standalone") {
      // In standalone mode, navigate to entity detail page in same tab
      navigate(`/entity/${entityId}`);
    } else if (onEntityClick) {
      // In overlay mode, use the provided click handler (opens overlay)
      onEntityClick(entityId);
    }
  };

  const handleMiddleClick = (event: React.MouseEvent) => {
    if (event.button === 1) {
      event.preventDefault();
      // Middle-click always opens in new tab
      window.open(`/entity/${entityId}`, "_blank");
    }
  };
  // If description is provided, use Box layout for multi-line content
  if (description) {
    return onEntityClick || mode === "standalone" ? (
      <Box
        sx={{
          cursor: "pointer",
          "&:hover .clickable-name": {
            color: "primary.light",
          },
        }}
        onClick={handleClick}
        onMouseDown={handleMiddleClick}
      >
        <Typography
          className="clickable-name"
          variant={variant}
          sx={{
            fontWeight,
            color: "text.primary", // Normal text color by default
            textDecoration: "none", // No underline by default
          }}
        >
          {name}
        </Typography>
        <Typography variant={descriptionVariant} color={descriptionColor}>
          {description}
        </Typography>
      </Box>
    ) : (
      <Box>
        <Typography variant={variant} sx={{ fontWeight }}>
          {name}
        </Typography>
        <Typography variant={descriptionVariant} color={descriptionColor}>
          {description}
        </Typography>
      </Box>
    );
  }

  // Single-line layout (original behavior)
  return onEntityClick || mode === "standalone" ? (
    <Typography
      variant={variant}
      sx={{
        fontWeight,
        color: "text.primary", // Normal text color by default
        cursor: "pointer",
        textDecoration: "none", // No underline by default
        "&:hover": {
          color: "primary.main", // Blue on hover
        },
      }}
      onClick={handleClick}
      onMouseDown={handleMiddleClick}
    >
      {name}
    </Typography>
  ) : (
    <Typography variant={variant} sx={{ fontWeight }}>
      {name}
    </Typography>
  );
};

export default ClickableEntityName;
